import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, KeyRound, ArrowLeft } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";
import { securityQa } from "@/lib/securityQa";
import { toast } from "sonner";

export default function ForgotPassword() {
  const navigate = useNavigate();
  // 0 = email, 1 = questions, 2 = done
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [ticket, setTicket] = useState("");
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState(["", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submitEmail = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await securityQa.initiateReset(email.trim().toLowerCase());
      if (!res.ok) {
        // Symmetric error from the backend — we don't disclose whether the email exists.
        setError(res.message || "Password reset isn't available for that account.");
        return;
      }
      setTicket(res.reset_ticket);
      setQuestions(res.questions);
      setAnswers(["", ""]);
      setStep(1);
    } catch (err) {
      setError(err.message || "Couldn't start password reset.");
    } finally {
      setBusy(false);
    }
  };

  const submitAnswers = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    const trimmed = answers.map((a) => (a || "").trim());
    if (trimmed.some((a) => a.length < 2)) {
      setError("Each answer must be at least 2 characters.");
      return;
    }
    setBusy(true);
    try {
      await securityQa.completeReset({
        reset_ticket: ticket,
        answers: questions.map((q, i) => ({ question_id: q.question_id, answer: trimmed[i] })),
        new_password: newPassword,
      });
      setStep(2);
      toast.success("Password reset. You can sign in now.");
    } catch (err) {
      setError(err.message || "Reset failed.");
      // If the ticket has expired, push back to step 0 so they can restart.
      if (/expired|start over/i.test(err.message || "")) {
        setStep(0);
        setTicket("");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10 bg-[var(--bg)] text-[var(--text)]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 border border-[var(--border)] mb-5">
            <KeyRound size={18} className="text-[var(--accent)]" />
          </div>
          <h1 className="font-mono-ui text-[11px] tracking-[0.4em] uppercase text-[var(--text-muted)] mb-4">
            Humane Behavior AI
          </h1>
          <p className="font-serif italic text-3xl leading-tight">
            {step === 0 ? "Reset password." : step === 1 ? "Verify it's you." : "All set."}
          </p>
          <p className="font-sans text-sm text-[var(--text-muted)] mt-3 leading-relaxed">
            {step === 0
              ? "Q&A is the only way to reset. We don't send password reset emails — security questions you answered at signup are the proof of identity."
              : step === 1
              ? `Answer the 2 questions below to set a new password. They were picked at random from the 5 you set when you signed up.`
              : "Sign in with your new password."}
          </p>
        </div>

        {step === 0 && (
          <form onSubmit={submitEmail} className="space-y-5" data-testid="reset-email-form">
            <label className="block">
              <span className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-dim)]">
                Email
              </span>
              <input
                type="email"
                required
                autoFocus
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="reset-email"
                className="mt-2 w-full bg-transparent border-b border-[var(--border)] focus:border-[var(--accent)] outline-none py-2 text-base transition-colors"
              />
            </label>
            {error && (
              <p data-testid="reset-error" className="font-sans text-sm text-red-400 border-l-2 border-red-400 pl-3">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit-email"
              className="w-full py-3 mt-2 font-mono-ui text-[11px] tracking-[0.3em] uppercase border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2"
            >
              {busy && <Loader2 size={12} className="animate-spin" />}
              {busy ? "Looking up…" : "Continue"}
            </button>
          </form>
        )}

        {step === 1 && (
          <form onSubmit={submitAnswers} className="space-y-5" data-testid="reset-answers-form">
            {questions.map((q, i) => (
              <label key={q.question_id} className="block">
                <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
                  {q.text}
                </span>
                <input
                  type="text"
                  required
                  minLength={2}
                  maxLength={120}
                  value={answers[i]}
                  onChange={(ev) => {
                    const next = [...answers];
                    next[i] = ev.target.value;
                    setAnswers(next);
                  }}
                  data-testid={`reset-answer-${i}`}
                  autoComplete="off"
                  autoFocus={i === 0}
                  className="mt-2 w-full bg-transparent border-b border-[var(--border)] focus:border-[var(--accent)] outline-none py-2 text-base transition-colors"
                />
              </label>
            ))}
            <label className="block">
              <span className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-dim)]">
                New password (min 8)
              </span>
              <PasswordInput
                required
                minLength={8}
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="reset-new-password"
                eyeTestId="reset-new-password-toggle"
                wrapperClassName="mt-2"
                className="text-base"
              />
            </label>
            <label className="block">
              <span className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-dim)]">
                Confirm new password
              </span>
              <PasswordInput
                required
                minLength={8}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="reset-confirm-password"
                eyeTestId="reset-confirm-password-toggle"
                wrapperClassName="mt-2"
                className="text-base"
              />
            </label>
            {error && (
              <p data-testid="reset-error" className="font-sans text-sm text-red-400 border-l-2 border-red-400 pl-3">
                {error}
              </p>
            )}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setStep(0);
                  setError("");
                }}
                data-testid="reset-back"
                className="flex-1 py-3 font-mono-ui text-[11px] tracking-[0.3em] uppercase border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] hover:border-[var(--text)] transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2"
              >
                <ArrowLeft size={11} /> Back
              </button>
              <button
                type="submit"
                disabled={busy}
                data-testid="reset-submit"
                className="flex-[2] py-3 font-mono-ui text-[11px] tracking-[0.3em] uppercase border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2"
              >
                {busy && <Loader2 size={12} className="animate-spin" />}
                {busy ? "Resetting…" : "Reset password"}
              </button>
            </div>
          </form>
        )}

        {step === 2 && (
          <div className="text-center" data-testid="reset-success">
            <p className="font-prose text-xl text-[var(--text)] mb-6">
              Your password is updated. Sign in below with the new one.
            </p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              data-testid="reset-to-login"
              className="w-full py-3 font-mono-ui text-[11px] tracking-[0.3em] uppercase border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] transition-colors"
            >
              Go to sign in
            </button>
          </div>
        )}

        <p className="font-sans text-sm text-[var(--text-muted)] text-center mt-8">
          Remembered it?{" "}
          <Link to="/login" data-testid="reset-to-login-link" className="text-[var(--accent)] hover:underline">
            Sign in.
          </Link>
        </p>

        <p className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)] text-center mt-6">
          Stuck?{" "}
          <a href="mailto:support@humanebehavior.com" className="hover:text-[var(--accent)] transition-colors">
            support@humanebehavior.com
          </a>
        </p>
      </div>
    </div>
  );
}
